 
import { BaseTableService } from '@/services/ui/table/base-table.service';
import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  input,
} from '@angular/core';
import { MatTableBaseComponent } from '../mat-table-base/mat-table-base.component';
import { MatTableService } from '../mat-table-base/mat-table.service';

/**
 * Base Table Wrapper Component
 *
 * This is a wrapper that accepts any table implementation through BaseTableService.
 * The component automatically uses the correct underlying implementation.
 *
 * Currently supports:
 * - MatTable (default)
 * - AG Grid (future)
 *
 * Usage:
 * ```html
 * <app-base-table [table]="table" />
 * ```
 *
 * Do not put tw:h-full on the host: consumers often set tw:h-[calc(...)] on this element; two
 * height utilities fight in Tailwind CSS order and 100% can win with an indefinite parent,
 * collapsing fill-parent tables. Use a flex column wrapper with bounded height + tw:flex-1
 * on this host, or put tw:h-full / tw:h-[calc] only on this host (not both).
 */
@Component({
  selector: 'app-base-table',
  imports: [MatTableBaseComponent],
  host: {
    class:
      'tw:flex tw:min-h-0 tw:min-w-0 tw:w-full tw:max-w-full tw:flex-1 tw:flex-col',
  },
  template: `
    <app-mat-table-base
      class="tw:flex tw:h-full tw:min-h-0 tw:min-w-0 tw:flex-1 tw:flex-col"
      #matTableBase
      [table]="getMatTableService()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseTableComponent<T = any> {
  readonly table = input.required<BaseTableService<T>>();

  @ViewChild('matTableBase', { static: false })
  matTableBaseComponent?: MatTableBaseComponent<T>;

  /**
   * Get the table as MatTableService
   * This is safe because all current implementations are MatTableService
   * In the future, we can add type detection here
   */
  protected getMatTableService(): MatTableService<T> {
    return this.table() as MatTableService<T>;
  }

  /**
   * Reset scroll position of the table
   */
  resetScrollPosition(): void {
    this.matTableBaseComponent?.resetScrollPosition();
  }

  /** Run change detection on the table so it re-renders after external data updates. */
  runChangeDetection(): void {
    this.matTableBaseComponent?.runChangeDetection();
  }
}
